import { showNotificationWarningFx } from '@/shared/lib/providers'
import { HidingSpin, PrimaryButton } from '@/shared/ui'
import { Flex, InputNumber } from 'antd'
import { useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { myWaveQuery } from '../../api/my-wave.api'
import {
  DEFAULT_MYWAVE_FREQUENCY,
  MAX_MYWAVE_FREQUENCY,
  MIN_MYWAVE_FREQUENCY,
} from '../../lib/constants'
import { getPureWaveSettingsRequestByFormValues } from '../../lib/get-my-wave-request'
import {
  type MyWaveFormModel,
  myWaveSchema,
} from '../../lib/my-wave-form.schema'
import {
  $queryLoading,
  $updateLoading,
  MyWaveGate,
  myWaveSettingsUpdated,
} from '../../model/my-wave-form.model'
import styles from './styles.module.scss'

export function MyWaveForm() {
  const { data: initialValues } = useUnit(myWaveQuery)
  const queryLoading = useUnit($queryLoading)
  const updateLoading = useUnit($updateLoading)
  const updateSettings = useUnit(myWaveSettingsUpdated)

  const formik = useFormik<MyWaveFormModel>({
    enableReinitialize: true,
    initialValues: {
      trackFrequency: initialValues?.trackFrequency ?? DEFAULT_MYWAVE_FREQUENCY,
      podcastFrequency:
        initialValues?.podcastFrequency ?? DEFAULT_MYWAVE_FREQUENCY,
    },
    validationSchema: toFormikValidationSchema(myWaveSchema),
    onSubmit: (values, { setSubmitting }) => {
      try {
        const parsed = myWaveSchema.safeParse(values)
        if (!parsed.success) {
          const errorMessages = parsed.error.errors
            .map((e) => e.message)
            .join('\n')
          void showNotificationWarningFx({ message: errorMessages })
          return
        }

        updateSettings(getPureWaveSettingsRequestByFormValues(values))
      } catch (err) {
        if (err instanceof z.ZodError) {
          void showNotificationWarningFx({
            message: err.errors.map((e) => e.message).join('\n'),
          })
        } else {
          void showNotificationWarningFx({
            message: 'Неизвестная ошибка при проверке формы',
          })
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleTrackFrequencyChange = (value: number | null) => {
    if (value != null) void formik.setFieldValue('trackFrequency', value)
  }

  const handlePodcastFrequencyChange = (value: number | null) => {
    if (value != null) void formik.setFieldValue('podcastFrequency', value)
  }

  return (
    <>
      <MyWaveGate />
      <HidingSpin spinning={queryLoading}>
        <Flex vertical gap={12}>
          <span className={styles.title}>Чередование треков и подкастов</span>
          <span className={styles.description}>
            Введите кол-во, например, 5 треков — 1 подкаст
          </span>
          <Flex align="center" gap={4}>
            <InputNumber
              value={formik.values.trackFrequency}
              className={styles.frequencyInput}
              min={MIN_MYWAVE_FREQUENCY}
              max={MAX_MYWAVE_FREQUENCY}
              controls
              onChange={handleTrackFrequencyChange}
            />
            <span>трек(-ов) -</span>
            <InputNumber
              value={formik.values.podcastFrequency}
              className={styles.frequencyInput}
              min={MIN_MYWAVE_FREQUENCY}
              max={MAX_MYWAVE_FREQUENCY}
              controls
              onChange={handlePodcastFrequencyChange}
            />
            <span>подкаст(-ов)</span>
          </Flex>
          <PrimaryButton
            disabled={!formik.dirty}
            loading={updateLoading}
            className={styles.saveButton}
            onClick={() => void formik.submitForm()}
          >
            Сохранить
          </PrimaryButton>
        </Flex>
      </HidingSpin>
    </>
  )
}
